import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { NoxnLogo } from "@/components/noxn/NoxnLogo";
import { WhoItsFor } from "@/components/noxn/WhoItsFor";
import { UseCases } from "@/components/noxn/UseCases";
import { BeforeAfter } from "@/components/noxn/BeforeAfter";
import { ObjectionHandler } from "@/components/noxn/ObjectionHandler";
import { Faq } from "@/components/noxn/Faq";

/**
 * NOXN — Minimal landing concept (route: /v2)
 * V2.1 changes:
 *   • All near-black (#0A0A0A) swapped for the design-system light navy (#1E3A6F).
 *   • Additional motion / micro-interactions: staggered hero reveal, animated
 *     scroll cue, hover lifts, arrow slides, cycling city-slash pulse.
 *   • "Continue as guest" friction escape hatch that routes to the public map.
 */

const INK = "#1E3A6F";        // light navy — new ink colour
const INK_TINT = (a) => `rgba(30,58,111,${a})`;
const ACCENT = "#FF5A00";

export default function LandingMinimal() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const reduce = useReducedMotion();

    const onBegin = () => {
        // Unified path: /book supports both authenticated + guest flows.
        // For staff/admin we still route to their dashboard.
        if (user && user.role !== "client") navigate("/dashboard");
        else navigate("/book");
    };
    const onGuest = () => navigate("/map");

    const fade = (delay = 0) =>
        reduce
            ? {}
            : {
                  initial: { opacity: 0, y: 18 },
                  whileInView: { opacity: 1, y: 0 },
                  viewport: { once: true, margin: "-60px" },
                  transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
              };

    const enter = (delay = 0) =>
        reduce
            ? {}
            : {
                  initial: { opacity: 0, y: 22 },
                  animate: { opacity: 1, y: 0 },
                  transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] },
              };

    return (
        <div
            data-testid="landing-minimal-root"
            className="min-h-screen bg-white font-body antialiased selection:bg-[#FF5A00] selection:text-white"
            style={{ color: INK }}
        >
            <MinimalHeader onBegin={onBegin} authed={!!user} />
            <StickyBookNudge onBegin={onBegin} />

            <main>
                <MinimalHero onBegin={onBegin} onGuest={onGuest} enter={enter} reduce={reduce} />
                <MinimalValue fade={fade} />
                <MinimalHow fade={fade} />
                <MinimalCities fade={fade} />
                <MinimalPricing onBegin={onBegin} fade={fade} />
                <MinimalFinal onBegin={onBegin} onGuest={onGuest} fade={fade} />
                <DetailAppendix onBegin={onBegin} />
            </main>

            <MinimalFooter />
        </div>
    );
}

/* ─────────────────────── detail appendix ──────────────────────────── */
/**
 * Progressive-disclosure "learn more" section. Renders selected legacy
 * (V1) sections wrapped in .landing-light so they inherit the white
 * theme + rounded card + lighter navy palette we already ship. Users
 * who are ready just tap the primary CTA above; users who want detail
 * scroll into this appendix.
 */
const DetailAppendix = ({ onBegin }) => (
    <>
        <section
            id="details"
            data-testid="v2-details-divider"
            className="border-t"
            style={{ borderColor: "rgba(30,58,111,0.10)" }}
        >
            <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16 lg:py-20 text-center">
                <div className="text-[11px] uppercase tracking-[0.28em] mb-4" style={{ color: "rgba(30,58,111,0.55)" }}>
                    Not sure yet?
                </div>
                <h2 className="font-display tracking-[-0.03em] text-3xl sm:text-4xl lg:text-5xl leading-[1.05] font-medium" style={{ color: "#1E3A6F" }}>
                    Here&apos;s the long version.
                </h2>
                <p className="mt-5 max-w-xl mx-auto text-[15px]" style={{ color: "rgba(30,58,111,0.65)" }}>
                    Who NOXN suits, real use cases, before/after outcomes, common objections
                    answered, and every FAQ we&apos;ve heard from small brands.
                </p>
                <motion.div
                    animate={{ y: [0, 6, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em]"
                    style={{ color: "#FF5A00" }}
                >
                    <ChevronDown size={14} strokeWidth={2} /> Scroll to explore
                </motion.div>
            </div>
        </section>

        {/* landing-light wrapper triggers all the white-theme + lighter-navy
            overrides in index.css that already ship with V1. */}
        <div className="landing-light">
            <WhoItsFor onBegin={onBegin} />
            <UseCases onBegin={onBegin} />
            <BeforeAfter onBegin={onBegin} />
            <ObjectionHandler />
            <Faq />
        </div>
    </>
);

/* ─────────────────────── sticky book nudge ────────────────────────── */
const StickyBookNudge = ({ onBegin }) => {
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const on = () => {
            const past = window.scrollY > 800;
            const nearBottom = (window.innerHeight + window.scrollY) >= (document.body.scrollHeight - 180);
            setVisible(past && !nearBottom);
        };
        on();
        window.addEventListener("scroll", on, { passive: true });
        window.addEventListener("resize", on);
        return () => {
            window.removeEventListener("scroll", on);
            window.removeEventListener("resize", on);
        };
    }, []);

    return (
        <motion.div
            initial={false}
            animate={{ opacity: visible ? 1 : 0, y: visible ? 0 : 20, pointerEvents: visible ? "auto" : "none" }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-6 right-6 z-40"
            data-testid="v2-sticky-nudge"
        >
            <button
                onClick={onBegin}
                data-testid="v2-sticky-cta"
                className="group inline-flex items-center gap-2 text-white text-[13px] font-medium pl-5 pr-5 py-3.5 rounded-full transition-all duration-300"
                style={{
                    backgroundColor: INK,
                    boxShadow: "0 18px 40px -14px rgba(30,58,111,0.35)",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = ACCENT)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = INK)}
            >
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ACCENT }} />
                Put Me On A Billboard
                <ArrowRight size={14} strokeWidth={2} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
        </motion.div>
    );
};

/* ────────────────────────────── header ────────────────────────────── */
const MinimalHeader = ({ onBegin, authed }) => {
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const on = () => setScrolled(window.scrollY > 6);
        on();
        window.addEventListener("scroll", on, { passive: true });
        return () => window.removeEventListener("scroll", on);
    }, []);

    return (
        <header
            data-testid="v2-header"
            className={`fixed inset-x-0 top-0 z-40 transition-all duration-300 ${
                scrolled ? "bg-white/85 backdrop-blur border-b" : "bg-transparent border-b border-transparent"
            }`}
            style={scrolled ? { borderColor: INK_TINT(0.08) } : {}}
        >
            <div className="mx-auto max-w-6xl px-6 lg:px-10 h-16 flex items-center justify-between">
                <a href="#top" data-testid="v2-logo" className="flex items-center group">
                    <NoxnLogo variant="navy" className="h-5 w-auto transition-transform duration-300 group-hover:-rotate-1" />
                </a>

                <nav className="hidden md:flex items-center gap-8" data-testid="v2-nav">
                    {[
                        { href: "#how", label: "How it works" },
                        { href: "#pricing", label: "Pricing" },
                        { href: "#details", label: "Details" },
                    ].map((n) => (
                        <a
                            key={n.href}
                            href={n.href}
                            data-testid={`v2-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="relative text-[13px] transition-colors group"
                            style={{ color: INK_TINT(0.7) }}
                        >
                            <span className="group-hover:text-[color:var(--ink)]" style={{ ["--ink"]: INK }}>{n.label}</span>
                            <span className="absolute left-0 -bottom-1 h-px w-0 bg-current transition-all duration-300 group-hover:w-full" />
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-6">
                    <Link
                        to="/map"
                        data-testid="v2-nav-our-screens"
                        className="hidden sm:inline relative text-[13px] transition-colors group"
                        style={{ color: INK_TINT(0.7) }}
                    >
                        <span className="group-hover:text-[color:var(--ink)]" style={{ ["--ink"]: INK }}>
                            Our Screens
                        </span>
                        <span className="absolute left-0 -bottom-1 h-px w-0 bg-current transition-all duration-300 group-hover:w-full" />
                    </Link>
                    <Link
                        to={authed ? "/account" : "/login"}
                        data-testid="v2-signin"
                        className="hidden sm:inline relative text-[13px] transition-colors group"
                        style={{ color: INK_TINT(0.7) }}
                    >
                        <span className="group-hover:text-[color:var(--ink)]" style={{ ["--ink"]: INK }}>
                            {authed ? "My account" : "Sign in"}
                        </span>
                        <span className="absolute left-0 -bottom-1 h-px w-0 bg-current transition-all duration-300 group-hover:w-full" />
                    </Link>
                    <button
                        onClick={onBegin}
                        data-testid="v2-header-cta"
                        className="group inline-flex items-center gap-2 text-white text-[13px] font-medium px-5 py-2.5 rounded-full transition-all duration-300 hover:shadow-lg"
                        style={{ backgroundColor: INK, boxShadow: `0 0 0 rgba(30,58,111,0)` }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = ACCENT)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = INK)}
                    >
                        Put Me On A Billboard
                        <ArrowRight size={14} strokeWidth={2} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                </div>
            </div>
        </header>
    );
};

/* ─────────────────────────────── hero ─────────────────────────────── */
const MinimalHero = ({ onBegin, onGuest, enter, reduce }) => (
    <section id="top" data-testid="v2-hero" className="relative pt-40 pb-32 lg:pt-56 lg:pb-44 overflow-hidden">
        {/* Ambient orange whisper — barely there, adds warmth */}
        <div
            className="pointer-events-none absolute -top-40 -right-20 w-[520px] h-[520px] rounded-full opacity-30 blur-[110px]"
            style={{ background: `radial-gradient(circle, ${ACCENT}66, transparent 60%)` }}
        />

        <div className="mx-auto max-w-6xl px-6 lg:px-10 relative">
            <motion.div
                {...enter(0)}
                className="text-[11px] uppercase tracking-[0.28em] mb-8 inline-flex items-center gap-2"
                style={{ color: INK_TINT(0.5) }}
            >
                <motion.span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: ACCENT }}
                    animate={reduce ? {} : { opacity: [1, 0.35, 1], scale: [1, 1.25, 1] }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                />
                Digital billboards, simplified
            </motion.div>

            <motion.h1
                {...enter(0.08)}
                data-testid="v2-hero-headline"
                className="font-display tracking-[-0.045em] text-[3.4rem] sm:text-[4.5rem] lg:text-[6.5rem] leading-[0.98] font-medium max-w-5xl"
                style={{ color: INK }}
            >
                Put your brand
                <br />
                <span style={{ color: ACCENT }}>where the city looks.</span>
            </motion.h1>

            <motion.p
                {...enter(0.2)}
                className="mt-10 max-w-xl text-[17px] sm:text-lg leading-relaxed"
                style={{ color: INK_TINT(0.6) }}
            >
                NOXN gives small brands direct access to Australia&apos;s premium digital
                billboards. Pick a screen, upload artwork, go live. Nothing else.
            </motion.p>

            <motion.div {...enter(0.32)} className="mt-12 flex flex-wrap items-center gap-6">
                <button
                    onClick={onBegin}
                    data-testid="v2-hero-cta"
                    className="group inline-flex items-center gap-2 text-white text-sm font-medium px-7 py-4 rounded-full transition-all duration-300"
                    style={{ backgroundColor: INK }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = ACCENT;
                        e.currentTarget.style.boxShadow = "0 18px 40px -14px rgba(255,90,0,0.55)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = INK;
                        e.currentTarget.style.boxShadow = "";
                    }}
                >
                    Put Me On A Billboard
                    <ArrowRight size={16} strokeWidth={2} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <a
                    href="#how"
                    data-testid="v2-hero-secondary"
                    className="text-sm underline underline-offset-4 transition-all duration-300"
                    style={{ color: INK_TINT(0.7), textDecorationColor: INK_TINT(0.2) }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.color = INK;
                        e.currentTarget.style.textDecorationColor = INK;
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.color = INK_TINT(0.7);
                        e.currentTarget.style.textDecorationColor = INK_TINT(0.2);
                    }}
                >
                    How it works
                </a>
            </motion.div>

            {/* Guest escape hatch — friction-reducing secondary path */}
            <motion.button
                {...enter(0.44)}
                onClick={onGuest}
                data-testid="v2-hero-guest"
                className="group mt-6 inline-flex items-center gap-2 text-[13px] transition-colors"
                style={{ color: INK_TINT(0.55) }}
                onMouseEnter={(e) => (e.currentTarget.style.color = ACCENT)}
                onMouseLeave={(e) => (e.currentTarget.style.color = INK_TINT(0.55))}
            >
                <span className="w-8 h-px transition-all duration-300 group-hover:w-12" style={{ backgroundColor: "currentColor" }} />
                No sign-up needed to browse
                <span className="transition-transform duration-300 group-hover:translate-x-1">
                    Continue as guest →
                </span>
            </motion.button>
        </div>

        {/* Scroll cue */}
        {!reduce && (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 0.9 }}
                className="pointer-events-none absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
                style={{ color: INK_TINT(0.4) }}
            >
                <span className="text-[10px] uppercase tracking-[0.28em]">Scroll</span>
                <motion.div
                    animate={{ y: [0, 6, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                >
                    <ChevronDown size={14} strokeWidth={2} />
                </motion.div>
            </motion.div>
        )}
    </section>
);

/* ────────────────────────────── value ─────────────────────────────── */
const MinimalValue = ({ fade }) => {
    const points = [
        { k: "Live inventory", v: "Every screen on the map is bookable in real time." },
        { k: "No lock-ins", v: "Pay per play. Run for a week or run for a quarter." },
        { k: "One person, one plan", v: "A campaign director on your booking end-to-end." },
    ];
    return (
        <section
            data-testid="v2-value"
            className="border-t py-24 lg:py-32"
            style={{ borderColor: INK_TINT(0.1) }}
        >
            <div className="mx-auto max-w-6xl px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
                <motion.h2
                    {...fade(0)}
                    className="lg:col-span-5 font-display tracking-[-0.03em] text-3xl sm:text-4xl lg:text-5xl leading-[1.05] font-medium"
                    style={{ color: INK }}
                >
                    Outdoor advertising, without the outdoor-advertising process.
                </motion.h2>
                <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-6">
                    {points.map((p, i) => (
                        <motion.div
                            key={p.k}
                            {...fade(0.08 * i)}
                            data-testid={`v2-value-${i}`}
                            whileHover={{ y: -4 }}
                            transition={{ type: "spring", stiffness: 260, damping: 22 }}
                            className="group cursor-default"
                        >
                            <div className="font-display text-xl mb-3 transition-all duration-300 group-hover:tracking-[0.05em]" style={{ color: ACCENT }}>
                                0{i + 1}
                            </div>
                            <div className="font-display text-base font-medium mb-2" style={{ color: INK }}>
                                {p.k}
                            </div>
                            <div className="text-sm leading-relaxed" style={{ color: INK_TINT(0.6) }}>
                                {p.v}
                            </div>
                            <div className="mt-4 h-px w-6 transition-all duration-500 group-hover:w-16" style={{ backgroundColor: ACCENT }} />
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

/* ────────────────────────── how it works ──────────────────────────── */
const MinimalHow = ({ fade }) => {
    const steps = [
        { t: "Pick a screen", b: "Browse live billboards on our map. Filter by city, format, and traffic." },
        { t: "Upload artwork", b: "Drop your finished creative. We verify specs and coordinate the operator." },
        { t: "Go live", b: "Pay online, watch it light up, and get a verified play log after the run." },
    ];
    return (
        <section id="how" data-testid="v2-how" className="border-t py-24 lg:py-32" style={{ borderColor: INK_TINT(0.1) }}>
            <div className="mx-auto max-w-6xl px-6 lg:px-10">
                <motion.div {...fade(0)} className="max-w-2xl mb-16 lg:mb-20">
                    <div className="text-[11px] uppercase tracking-[0.28em] mb-6" style={{ color: INK_TINT(0.5) }}>
                        The full flow
                    </div>
                    <h2 className="font-display tracking-[-0.03em] text-3xl sm:text-4xl lg:text-5xl leading-[1.05] font-medium" style={{ color: INK }}>
                        Three steps, one browser tab.
                    </h2>
                </motion.div>

                <div
                    className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x border-t border-b"
                    style={{ borderColor: INK_TINT(0.1), ["--divide"]: INK_TINT(0.1) }}
                >
                    {steps.map((s, i) => (
                        <motion.div
                            key={s.t}
                            {...fade(0.06 * i)}
                            data-testid={`v2-step-${i}`}
                            whileHover={{ y: -3 }}
                            transition={{ type: "spring", stiffness: 260, damping: 22 }}
                            className="group relative py-10 md:py-14 md:px-10 first:md:pl-0 last:md:pr-0 overflow-hidden cursor-default"
                            style={{ borderColor: INK_TINT(0.1) }}
                        >
                            {/* Hovering orange sliver from the left */}
                            <span
                                className="absolute top-0 left-0 h-full w-0 transition-all duration-500 group-hover:w-1"
                                style={{ backgroundColor: ACCENT }}
                            />
                            <div
                                className="font-display text-6xl leading-none tracking-tighter mb-6 transition-colors duration-500"
                                style={{ color: INK_TINT(0.15) }}
                            >
                                <span className="group-hover:hidden">{String(i + 1).padStart(2, "0")}</span>
                                <span className="hidden group-hover:inline" style={{ color: ACCENT }}>
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                            </div>
                            <div className="font-display text-lg font-medium mb-3" style={{ color: INK }}>
                                {s.t}
                            </div>
                            <div className="text-sm leading-relaxed max-w-xs" style={{ color: INK_TINT(0.6) }}>
                                {s.b}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

/* ────────────────────────────── cities ────────────────────────────── */
const MinimalCities = ({ fade }) => {
    const cities = ["Brisbane", "Sydney", "Melbourne", "Perth", "Adelaide"];
    return (
        <section data-testid="v2-cities" className="py-20 lg:py-24">
            <div className="mx-auto max-w-6xl px-6 lg:px-10">
                <motion.div {...fade(0)} className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-8">
                    <div className="text-[11px] uppercase tracking-[0.28em]" style={{ color: INK_TINT(0.5) }}>
                        Live in
                    </div>
                    <div className="flex flex-wrap gap-x-8 gap-y-3 font-display text-2xl sm:text-3xl lg:text-4xl tracking-[-0.02em] font-medium">
                        {cities.map((c, i) => (
                            <motion.span
                                key={c}
                                data-testid={`v2-city-${i}`}
                                className="group cursor-default"
                                whileHover={{ y: -2 }}
                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                            >
                                <span style={{ color: INK }} className="transition-colors duration-300 group-hover:text-[color:var(--acc)]" data-acc={ACCENT}>
                                    {c}
                                </span>
                                {i < cities.length - 1 && (
                                    <motion.span
                                        className="px-3 inline-block"
                                        style={{ color: ACCENT }}
                                        animate={{ opacity: [0.4, 1, 0.4] }}
                                        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut", delay: i * 0.35 }}
                                    >
                                        /
                                    </motion.span>
                                )}
                            </motion.span>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

/* ────────────────────────────── pricing ───────────────────────────── */
const MinimalPricing = ({ onBegin, fade }) => (
    <section id="pricing" data-testid="v2-pricing" className="border-t py-24 lg:py-32" style={{ borderColor: INK_TINT(0.1) }}>
        <div className="mx-auto max-w-6xl px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 items-end">
            <motion.div {...fade(0)} className="lg:col-span-7">
                <div className="text-[11px] uppercase tracking-[0.28em] mb-6" style={{ color: INK_TINT(0.5) }}>
                    Pricing
                </div>
                <h2 className="font-display tracking-[-0.03em] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] font-medium" style={{ color: INK }}>
                    From{" "}
                    <motion.span
                        style={{ color: ACCENT, display: "inline-block" }}
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                    >
                        $0.34
                    </motion.span>{" "}
                    per play.
                </h2>
                <p className="mt-6 max-w-lg text-[15px] leading-relaxed" style={{ color: INK_TINT(0.6) }}>
                    Buy in bundles from 200 plays. No agency retainer, no monthly
                    minimum. GST added at checkout, receipts emailed the moment
                    you pay.
                </p>
            </motion.div>

            <motion.div {...fade(0.1)} className="lg:col-span-5 lg:justify-self-end">
                <button
                    onClick={onBegin}
                    data-testid="v2-pricing-cta"
                    className="group inline-flex items-center gap-2 text-white text-sm font-medium px-7 py-4 rounded-full transition-all duration-300"
                    style={{ backgroundColor: INK }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = ACCENT;
                        e.currentTarget.style.boxShadow = "0 18px 40px -14px rgba(255,90,0,0.55)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = INK;
                        e.currentTarget.style.boxShadow = "";
                    }}
                >
                    See live tiers
                    <ArrowRight size={16} strokeWidth={2} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
            </motion.div>
        </div>
    </section>
);

/* ─────────────────────────── final CTA ────────────────────────────── */
const MinimalFinal = ({ onBegin, onGuest, fade }) => (
    <section data-testid="v2-final" className="px-6 lg:px-10 pb-24 lg:pb-32">
        <motion.div
            {...fade(0)}
            className="mx-auto max-w-6xl text-white rounded-3xl px-8 py-20 lg:py-28 text-center overflow-hidden relative"
            style={{ backgroundColor: INK }}
        >
            <div
                className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full opacity-45 blur-[100px]"
                style={{ background: `radial-gradient(circle, ${ACCENT}8c, transparent 65%)` }}
            />
            <motion.h2
                {...fade(0.08)}
                className="relative font-display tracking-[-0.035em] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] font-medium max-w-3xl mx-auto"
            >
                Your first billboard could go live this week.
            </motion.h2>
            <motion.p
                {...fade(0.16)}
                className="relative mt-6 max-w-lg mx-auto text-[15px] text-white/60"
            >
                Book a screen in about four minutes. Cancel anytime before it
                goes live, no questions asked.
            </motion.p>
            <motion.div {...fade(0.24)} className="relative mt-12 flex flex-col items-center gap-4">
                <button
                    onClick={onBegin}
                    data-testid="v2-final-cta"
                    className="group inline-flex items-center gap-2 bg-white text-sm font-medium px-8 py-4 rounded-full transition-all duration-300 hover:bg-[#FF5A00] hover:text-white"
                    style={{ color: INK }}
                >
                    Put Me On A Billboard
                    <ArrowRight size={16} strokeWidth={2} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <button
                    onClick={onGuest}
                    data-testid="v2-final-guest"
                    className="group text-[13px] text-white/60 hover:text-white transition-colors duration-300 inline-flex items-center gap-2"
                >
                    <span className="w-8 h-px bg-white/30 transition-all duration-300 group-hover:w-12 group-hover:bg-white" />
                    Continue as guest — browse screens first
                    <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                </button>
            </motion.div>
        </motion.div>
    </section>
);

/* ────────────────────────────── footer ────────────────────────────── */
const MinimalFooter = () => (
    <footer data-testid="v2-footer" className="border-t py-10" style={{ borderColor: INK_TINT(0.1) }}>
        <div className="mx-auto max-w-6xl px-6 lg:px-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-[13px]" style={{ color: INK_TINT(0.55) }}>
            <div className="flex items-center gap-3">
                <NoxnLogo variant="navy" className="h-4 w-auto" />
                <span>© {new Date().getFullYear()} NOXN</span>
            </div>
            <div className="flex items-center gap-6">
                <a
                    href="mailto:noxn@noxn.com.au"
                    className="transition-colors duration-300"
                    style={{ color: "inherit" }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = INK)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "")}
                    data-testid="v2-footer-email"
                >
                    noxn@noxn.com.au
                </a>
                <Link
                    to="/classic"
                    className="transition-colors duration-300"
                    style={{ color: "inherit" }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = INK)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "")}
                    data-testid="v2-footer-classic"
                >
                    Classic site
                </Link>
            </div>
        </div>
    </footer>
);
